import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from 'react';
import WelcomeMessageSendForm from './components/WelcomeMessageSendForm';
import LoadingSpinner from 'src/components/LoadingSpinner';
import TabbedDrawer from 'src/components/TabbedDrawer';
import Handlebars from 'src/util/handlebarsHelpers';
import debounce from 'src/util/debounce';
import useUser from 'src/contexts/useUser';
import gql from 'graphql-tag';
import Query from 'src/components/GraphQL/components/Query';
import { Mutation, withApollo } from 'react-apollo';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Tooltip from 'src/components/Tooltip';
import ThemedButton from 'src/components/Forms/components/ThemedButton';
import HandlebarsExampleTabs from './components/HandlebarsExampleTabs';
import { AlertTriangle } from 'react-feather';
import Modal from 'src/components/Modal';
import TimedResponseMessage from 'src/components/TimedResponseMessage';
import RichTextEditor from 'src/components/RichTextEditor';
import 'react-tabs/style/react-tabs.css';
import 'graphiql/graphiql.css';
import moment from 'moment';

import './styles.scss';

/*
Combining 100% height and flex box (using absolute positioning)
https://www.codementor.io/@stephenbunch/how-to-make-a-scrollable-container-with-dynamic-height-using-flexbox-bkaunxewg
*/
import {
  buildSuggestions,
  // createSuggestion,
} from './util/autocompleteProvider';
import { validateTemplateBindings } from './util/validateTemplateBindings';
import {
  parseSubjectAndBodyFromEmailHtml,
  getGuestPrimaryEmailFromTemplateData,
} from './util/deriveEmailSubjectFromHtml';

function graphqlErrorMessage(error) {
  if (error?.graphQLErrors?.length) {
    return error.graphQLErrors.map(e => e.message).join('; ');
  }
  if (error?.networkError?.message) {
    return error.networkError.message;
  }
  return error?.message || 'Unable to load template.';
}

/** Comma-separated CC defaults from guest.emailAddresses (unique; omits primaryEmailAddress). */
function formatGuestCcDefault(guestEmailAddresses, primaryEmail) {
  if (!guestEmailAddresses?.length) return '';
  const primaryKey = (primaryEmail || '').trim().toLowerCase();
  const seen = new Set();
  const parts = [];
  for (const row of guestEmailAddresses) {
    const addr = (row?.emailAddress || '').trim();
    if (!addr) continue;
    const key = addr.toLowerCase();
    if (primaryKey && key === primaryKey) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    parts.push(addr);
  }
  return parts.join(', ');
}

function parseCcStringForMutation(ccString, primaryEmail) {
  const raw = (ccString || '').trim();
  if (!raw) return null;
  const primaryKey = (primaryEmail || '').trim().toLowerCase();
  const parts = raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .filter(addr => !primaryKey || addr.toLowerCase() !== primaryKey);
  if (!parts.length) return null;
  return parts.map(emailAddress => ({ emailAddress }));
}

// https://www.npmjs.com/package/graphiql/v/1.3.2

const handlebarsHelperDefinitions = [
  {
    key: 'if',
    description:
      'Conditionally render a block. If its argument returns false, undefined, null, "", 0, or [], Handlebars will not render the block.',
  },
  {
    key: 'else',
    description:
      "If included within an 'if' block helper, it will render content if the parent condition proves false.",
  },
  {
    key: 'unless',
    description:
      'The inverse of the if helper. Its block will be rendered if the expression returns a falsy value.',
  },
  {
    key: 'each',
    description:
      "Iterate over a list. Use 'this' to reference the element being iterated over.",
  },
  {
    key: 'with',
    description:
      'Allows you to change the evaluation context of template-part.',
  },
  {
    key: 'lookup',
    description:
      'Allows for dynamic parameter resolution using Handlebars variables.',
  },
  {
    key: 'log',
    description:
      'Allows for logging of context state while executing a template.',
  },
  // {
  //   key: 'blockHelperMissing',
  //   description:
  //     'Called when a block-expression calls a helper that is not registered, but the name matches a property in the current evaluation context.',
  // },
  // {
  //   key: 'helperMissing',
  //   description:
  //     'Called when a mustache or a block-statement is not a registered helper AND is not a property of the current evaluation context.',
  // },
  {
    key: 'formatDate',
    description:
      'Takes a time in either epoch or ISO8601 format and converts it to a format you specify.',
  },
  {
    key: 'greaterThan',
    description:
      'Proves true if first value is greater than the second. Use as a block or in subexpressions, e.g. {{#if (gt a b)}}.',
  },
  {
    key: 'gt',
    description:
      'Alias for greater-than: returns a > b. Use in subexpressions: {{#if (gt a b)}} … {{else if (eq a b)}} ….',
  },
  {
    key: 'lessThan',
    description: 'Proves true if first value is less than the second value.',
  },
  {
    key: 'lt',
    description: 'Alias for lessThan (a < b). Use in subexpressions: {{#if (lt a b)}}.',
  },
  {
    key: 'equals',
    description: 'Proves true if first value is equal to the second value (==).',
  },
  {
    key: 'eq',
    description:
      'Alias for equals (==). Use in subexpressions: {{#if (eq a b)}} or {{else if (eq x y)}}.',
  },
  {
    key: 'gte',
    description: 'True if first value >= second (block or {{#if (gte a b)}}).',
  },
  {
    key: 'lte',
    description: 'True if first value <= second (block or {{#if (lte a b)}}).',
  },
  {
    key: 'notEquals',
    description: 'Proves true if first value not equal to the second value.',
  },
  {
    key: 'and',
    description: 'Proves true if first and second values are truthy.',
  },
  {
    key: 'or',
    description: 'Proves true if first or second values are truthy.',
  },
  {
    key: 'length',
    description: 'Returns the number of characters in a given string or array.',
  },
  {
    key: 'insert',
    description: 'Returns the value of the argument provided',
  },
  {
    key: 'urlEncode',
    description:
      'URL-encodes a value for query strings (same as encodeURIComponent). Example: &token={{urlEncode appToken}}',
  },
  {
    key: 'encodeURIComponent',
    description: 'Alias for urlEncode.',
  },
];

const MARK_WELCOME_AS_SENT = gql`
  mutation MarkGuestReservationWelcomeMessageSent($reference: String!) {
    markGuestReservationWelcomeMessageSent(where: { reference: $reference }) {
      id
    }
  }
`;

const MARK_WELCOME_AS_UNSENT = gql`
  mutation MarkGuestReservationWelcomeMessageUnsent($reference: String!) {
    markGuestReservationWelcomeMessageUnsent(where: { reference: $reference }) {
      id
    }
  }
`;

const UPDATE_TEMPLATE_BUILDER_DEFINITION = gql`
  mutation updateTemplateBuilderDefinition(
    $TemplateBuilderDefinitionUpdateInput: TemplateBuilderDefinitionUpdateInput
    $TemplateBuilderDefinitionWhereUniqueInput: TemplateBuilderDefinitionWhereUniqueInput
  ) {
    updateTemplateBuilderDefinition(
      where: $TemplateBuilderDefinitionWhereUniqueInput
      data: $TemplateBuilderDefinitionUpdateInput
    ) {
      body
    }
  }
`;

const SEND_EMAIL = gql`
  mutation SendEmail(
    $to: String!
    $subject: String!
    $message: String!
    $cc: [EmailAddress!]
  ) {
    sendEmail(
      data: { to: $to, subject: $subject, message: $message, cc: $cc }
    ) {
      messageId
    }
  }
`;

const NOTIFICATION_DEFINITION = gql`
  query TemplateBuilderDefinition($id: Int, $params: String) {
    templateBuilderDefinition(where: { id: $id }) {
      type
      sqlStatement
      body
      templateContext(params: $params) {
        data
        inputFields {
          type
          name
          value
          ... on TemplateBuilderSelectField {
            options {
              label
              value
            }
          }
        }
      }
    }

    templateBuilderRevisions(
      first: 20
      where: { templateBuilderDefinitionId: $id }
    ) {
      createdAt
      body
    }
  }
`;

const TemplateBuilder = ({
  definitionId,
  params,
  client,
  contextData,
  reference,
  onSuccess,
  welcomeSent,
  /** When set (e.g. from guestReservation guest query), used to prefill Preview and Send "To". */
  guestPrimaryEmail,
  /** Guest `emailAddresses` (e.g. from TYFR query) — prefill Preview/Send CC as comma-separated. */
  guestEmailAddresses,
  /** TYFR email route only — shows Preview and Send + send-email modal */
  showPreviewSendButton,
}) => {
  console.log('[TemplateBuilder] Component rendered with props:', {
    definitionId,
    params,
    client: !!client,
    contextData,
    reference,
    hasOnSuccess: !!onSuccess,
    welcomeSent,
    guestPrimaryEmail,
    guestEmailAddresses,
    showPreviewSendButton,
  });

  const {
    templateBody,
    templateData,
    inputFields,
    definitionRevisions,
  } = contextData?.state || {};

  console.log('[TemplateBuilder] Extracted from contextData.state:', {
    templateBody: !!templateBody,
    templateData: !!templateData,
    inputFields: !!inputFields,
    definitionRevisions: !!definitionRevisions,
    templateBodyLength: templateBody?.length,
    templateDataKeys: templateData ? Object.keys(templateData) : null,
  });

  const {
    setTemplateBody,
    setTemplateData,
    setInputFields,
    setDefinitionRevisions,
  } = contextData || {};

  console.log('[TemplateBuilder] Context methods available:', {
    hasSetTemplateBody: !!setTemplateBody,
    hasSetTemplateData: !!setTemplateData,
    hasSetInputFields: !!setInputFields,
    hasSetDefinitionRevisions: !!setDefinitionRevisions,
  });
  const urlParams = new URLSearchParams(location?.search);

  const disposables = useRef([]);
  const monacoContainer = useRef(null);
  const monacoEditor = useRef(null);
  const templateTextarea = useRef(null);
  const clipboardButton = useRef(null);
  const [bodyError, setBodyError] = useState(null);
  const [templateLoadError, setTemplateLoadError] = useState(null);
  const [loadErrorDismissed, setLoadErrorDismissed] = useState(false);
  const [supportValidationErrors, setSupportValidationErrors] = useState([]);
  const [missingFieldsBannerDismissed, setMissingFieldsBannerDismissed] =
    useState(false);
  const [compileErrorBannerDismissed, setCompileErrorBannerDismissed] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mutationFeedback, setMutationFeedback] = useState(false);
  const [updateTemplateLoading, setUpdateTemplateLoading] = useState(false);
  const [clipboardModalIsOpen, setClipboardModalIsOpen] = useState(false);
  const [welcomeSentMutationLoading, setWelcomeSentMutationLoading] = useState(
    false
  );
  const [markAsSentModalIsOpen, setMarkAsSentModalIsOpen] = useState(false);
  const [welcomeSentLabel, setWelcomeSentLabel] = useState(
    welcomeSent ? 'Mark as Unsent' : 'Mark as Sent'
  );
  const [alertShowTimeout, setAlertShowTimeout] = useState(false);
  const [emailHtml, setEmailHtml] = useState('');
  const [previewSendOpen, setPreviewSendOpen] = useState(false);
  const [previewTo, setPreviewTo] = useState('');
  const [previewCc, setPreviewCc] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBodyHtml, setPreviewBodyHtml] = useState('');
  const [sendEmailLoading, setSendEmailLoading] = useState(false);

  const Textarea = ({ compiledData }) => {
    const templateTextarea = useRef(null);
    const toggleDisabledClass = () => {
      const scrollTop = templateTextarea.current.scrollTop;
      const innerHeight = $(templateTextarea.current).innerHeight();
      const scrollHeight = templateTextarea.current.scrollHeight;

      if (scrollTop + innerHeight + 2 >= scrollHeight) {
        $(clipboardButton.current).removeClass('clipboard-button-disabled');
      } else {
        // $(clipboardButton.current).addClass('clipboard-button-disabled');
      }
    };
    const handleTextareaScroll = e => {
      toggleDisabledClass();
    };

    useEffect(() => {
      templateTextarea.current &&
        templateTextarea.current.addEventListener(
          'scroll',
          handleTextareaScroll
        );
      toggleDisabledClass();

      return () => {
        templateTextarea.current &&
          templateTextarea.current.removeEventListener(
            'scroll',
            handleTextareaScroll
          );
      };
    }, []);
    return (
      <textarea
        hidden={!definitionId}
        ref={templateTextarea}
        className={`form-control`}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
        }}
        value={compiledData}
        onChange={() => {}}
      />
    );
  };

  const getCompiledHandlebarsTemplate = (templateCode, data) => {
    try {
      const template = Handlebars.compile(templateCode); //
      const templateResult = template(data);
      return templateResult;
    } catch (err) {
      console.log('err', err);
      setBodyError(err?.message);
      console.log('message', err?.message);
      return false;
    }
  };

  const convertNewlinesToBr = (htmlString) => {
    if (!htmlString) return '';
    // Convert \n to <br />, but avoid double-converting if already HTML
    // Split by newlines and join with <br /> tags
    return htmlString.split('\n').join('<br />');
  };

  useEffect(() => {
    const cd = getCompiledHandlebarsTemplate(templateBody || '', templateData || {});
    if (cd !== false) {
      setEmailHtml(convertNewlinesToBr(cd || ''));
    }
  }, [templateBody, templateData]);

  const initializeMonacoEditor = updatedTemplateBody => {
    console.log('[TemplateBuilder] initializeMonacoEditor called:', {
      hasTemplateData: !!templateData,
      hasUpdatedTemplateBody: !!updatedTemplateBody,
      updatedTemplateBodyLength: updatedTemplateBody?.length,
      hasMonacoContainer: !!monacoContainer?.current,
      containerHeight: monacoContainer?.current?.clientHeight,
      containerWidth: monacoContainer?.current?.clientWidth,
      editorAlreadyExists: !!monacoEditor.current,
    });

    // Prevent re-initialization if editor already exists
    if (monacoEditor.current) {
      console.log('[TemplateBuilder] Editor already exists, skipping initialization');
      return;
    }

    if (!templateData || !updatedTemplateBody) {
      console.warn('[TemplateBuilder] initializeMonacoEditor early return:', {
        hasTemplateData: !!templateData,
        hasUpdatedTemplateBody: !!updatedTemplateBody,
      });
      return;
    }

    if (!monacoContainer?.current) {
      console.warn('[TemplateBuilder] Monaco container ref is null');
      return;
    }

    // const getTemplateBodyEditorValue = () => {
    //   if (updatedTemplateBody) {
    //     return updatedTemplateBody;
    //   }
    //   return templateBody || '';
    // };
    // const templateBodyEditorValue = getTemplateBodyEditorValue();

    const needsHash = key => {
      if (
        key === 'length' ||
        key === 'insert' ||
        key === 'formatDate'
        // ||
        // key === 'today'
      ) {
        return false;
      }
      return true;
    };
    const editorOptions = {
      mode: { name: 'handlebars', base: 'text/html' },
      minimap: {
        enabled: false,
      },
      wordWrap: 'on',
      lineNumbers: true,
      automaticLayout: true,
      scrollBeyondLastLine: false,
    };
    console.log('[TemplateBuilder] Starting Monaco loader initialization');
    window.monaco_loader.init().then(monaco => {
      console.log('[TemplateBuilder] Monaco loader initialized successfully');
      const properties = {
        value: updatedTemplateBody || '',
        language: 'handlebars',
        ...editorOptions,
      };

      console.log('[TemplateBuilder] Creating Monaco editor with properties:', {
        valueLength: properties.value?.length,
        language: properties.language,
        hasContainer: !!monacoContainer?.current,
      });

      const editor = monaco.editor.create(monacoContainer?.current, properties);
      monacoEditor.current = editor;
      editorInitializedRef.current = true;
      console.log('[TemplateBuilder] Monaco editor created successfully');

      // Function to update editor layout based on container size
      const updateEditorLayout = () => {
        if (monacoContainer?.current && editor) {
          const containerHeight = monacoContainer.current.clientHeight;
          const containerWidth = monacoContainer.current.clientWidth;
          if (containerHeight > 0 && containerWidth > 0) {
            editor.layout({ width: containerWidth, height: containerHeight });
          }
        }
      };

      // Initial layout update after a brief delay to ensure container has computed height
      setTimeout(() => {
        updateEditorLayout();
      }, 100);

      // Use ResizeObserver to update layout when container resizes
      if (monacoContainer?.current && window.ResizeObserver) {
        const resizeObserver = new ResizeObserver(() => {
          updateEditorLayout();
        });
        resizeObserver.observe(monacoContainer.current);
        disposables.current.push({
          dispose: () => resizeObserver.disconnect(),
        });
      }

      // Save state on focus/blur using DOM events
      const editorDomNode = editor.getDomNode();
      if (editorDomNode) {
        const handleFocus = () => {
          saveEditorState();
        };
        const handleBlur = () => {
          saveEditorState();
        };
        editorDomNode.addEventListener('focus', handleFocus, true);
        editorDomNode.addEventListener('blur', handleBlur, true);
        
        disposables.current.push({
          dispose: () => {
            editorDomNode.removeEventListener('focus', handleFocus, true);
            editorDomNode.removeEventListener('blur', handleBlur, true);
          },
        });
      }

      editor.onDidChangeModelContent(event => {
        // Save editor state continuously during typing
        saveEditorState();
        
        // Mark that user is typing to prevent external updates
        isUserTypingRef.current = true;
        
        // Clear any pending update timeout
        if (editorUpdateTimeoutRef.current) {
          clearTimeout(editorUpdateTimeoutRef.current);
        }
        
        // const value = model.getValue();
        const value = editor?.getValue();
        
        // Track this as the last value the user edited
        lastUserEditValueRef.current = value;

        const debounced = debounce(function() {
          // Save state again before triggering re-render
          saveEditorState();
          setTemplateBody(value);
          const transformedContentFromTemplate = getCompiledHandlebarsTemplate(
            value,
            templateData
          );
          if (transformedContentFromTemplate) {
            // setTransformedBodyContent(transformedContentFromTemplate);
            setBodyError(null);
          }
          
          // Reset typing flag after debounce completes, but add a small delay
          // to ensure the re-render has completed
          editorUpdateTimeoutRef.current = setTimeout(() => {
            isUserTypingRef.current = false;
            // Restore state after re-render completes (only scroll, not cursor to prevent shifts)
            requestAnimationFrame(() => {
              restoreEditorState(false, false); // Don't restore cursor during typing
            });
          }, 100);
        }, 500);
        debounced();
      });

      const provider = monaco.languages.registerCompletionItemProvider(
        'handlebars',
        {
          triggerCharacters: '{',
          provideCompletionItems: (model, position, context) => {
            const futureRangeValues = model.getValueInRange({
              startLineNumber: position.lineNumber,
              startColumn: position.column,
              endLineNumber: position.lineNumber,
              endColumn: position.column + 4,
            });
            const pastRangeValues = model.getValueInRange({
              startLineNumber: position.lineNumber,
              startColumn: position.column,
              endLineNumber: position.lineNumber,
              endColumn: position.column - 3,
            });

            const containsFutureTripleBraces =
              futureRangeValues.indexOf('}}}') === 1 ? true : false;
            const containsPastTripleBraces =
              pastRangeValues.indexOf('{{{') === 0 ? true : false;
            const containsTripleBraces =
              containsFutureTripleBraces || containsPastTripleBraces;

            const propSuggestions = Object.keys(templateData).reduce(
              (acc, data) => {
                acc.push({
                  label: data,
                  kind: monaco.languages.CompletionItemKind.Variable,
                  insertText: data,
                  insertTextRules:
                    monaco.languages.CompletionItemInsertTextRule
                      .InsertAsSnippet,
                  detail: String(templateData[data]),
                });
                return acc;
              },
              []
            );

            const handlebarsHelperSuggestions = Object.keys(
              Handlebars?.helpers
            ).reduce((acc, data) => {
              const helperMatch = handlebarsHelperDefinitions.find(
                definition => data === definition?.key
              );
              if (data === 'blockHelperMissing' || data === 'helperMissing') {
                return acc;
              }
              if (containsTripleBraces && needsHash(data)) {
                return acc;
              }
              acc.push({
                label: needsHash(data) ? `#${data}` : data,
                kind: monaco.languages.CompletionItemKind.Function,
                insertText: needsHash(data) ? `#${data}` : data,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                documentation: helperMatch?.description,
              });
              return acc;
            }, []);
            if (!containsTripleBraces) {
              handlebarsHelperSuggestions.push(
                {
                  label: 'else',
                  kind: monaco.languages.CompletionItemKind.Function,
                  insertText: 'else',
                  insertTextRules:
                    monaco.languages.CompletionItemInsertTextRule
                      .InsertAsSnippet,
                  documentation:
                    "If included within an 'if' block helper, it will render content when the parent condition proves false.",
                },
                {
                  label: 'else if',
                  kind: monaco.languages.CompletionItemKind.Function,
                  insertText: 'else if',
                  insertTextRules:
                    monaco.languages.CompletionItemInsertTextRule
                      .InsertAsSnippet,
                  documentation:
                    "If included within an 'if' block helper, it will render content when the parent condition proves false and it's argument proves true.",
                }
              );
            }
            const suggestions = buildSuggestions(
              monaco,

              [...propSuggestions, ...handlebarsHelperSuggestions],
              model,
              position
            );
            return {
              suggestions,
            };
          },
        }
      );

      disposables.current.push(provider);
    }).catch((error) => {
      console.error('[TemplateBuilder] Monaco loader initialization failed:', error);
    });
  };
  const getTemplateData = (updatedTemplateBody, user) => {
    console.log('[TemplateBuilder] getTemplateData called:', {
      definitionId,
      params,
      updatedTemplateBody: !!updatedTemplateBody,
      hasUser: !!user,
      hasClient: !!client,
    });

    if (!client) {
      console.error('[TemplateBuilder] Apollo client is not available! Cannot fetch template data.');
      setTemplateLoadError(
        'Unable to load template: Apollo client is not available.'
      );
      setSupportValidationErrors([]);
      setIsLoading(false);
      return;
    }

    const queryVariables = {
      id: Number(definitionId),
      params: params || null,
    };

    console.log('[TemplateBuilder] Query variables:', {
      id: queryVariables.id,
      idType: typeof queryVariables.id,
      params: queryVariables.params,
      paramsType: typeof queryVariables.params,
      isNaN: isNaN(queryVariables.id),
    });

    if (isNaN(queryVariables.id)) {
      console.error('[TemplateBuilder] Invalid definitionId - cannot convert to number:', definitionId);
      setTemplateLoadError('Invalid template definition.');
      setSupportValidationErrors([]);
      setIsLoading(false);
      return;
    }

    setTemplateLoadError(null);
    setSupportValidationErrors([]);
    setIsLoading(true);
    client
      .query({
        query: NOTIFICATION_DEFINITION,
        variables: queryVariables,
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        console.log('[TemplateBuilder] Query response received:', {
          hasData: !!data,
          hasDefinition: !!data?.templateBuilderDefinition,
          hasBody: !!data?.templateBuilderDefinition?.body,
          hasTemplateContext: !!data?.templateBuilderDefinition?.templateContext,
          hasContextData: !!data?.templateBuilderDefinition?.templateContext?.data,
          hasInputFields: !!data?.templateBuilderDefinition?.templateContext?.inputFields,
          hasRevisions: !!data?.templateBuilderRevisions,
        });
        if (data?.templateBuilderDefinition) {
          const rawContextData =
            data?.templateBuilderDefinition?.templateContext?.data || {};
          const rawValidationErrors =
            rawContextData.validation_errors ??
            rawContextData.validationErrors;
          const extractedValidationErrors = Array.isArray(rawValidationErrors)
            ? rawValidationErrors
            : [];
          const {
            validation_errors: _omitSnake,
            validationErrors: _omitCamel,
            ...restContext
          } = rawContextData;
          const newTemplateData = {
            ...restContext,
            gUser: user,
          };
          setSupportValidationErrors(extractedValidationErrors);
          const newTemplateBody =
            updatedTemplateBody ??
            data?.templateBuilderDefinition?.body ??
            '';
          const newInputFields = data?.templateBuilderDefinition?.templateContext?.inputFields;
          const newRevisions = data?.templateBuilderRevisions;

          console.log('[TemplateBuilder] Setting state values:', {
            templateDataKeys: Object.keys(newTemplateData),
            templateBodyLength: newTemplateBody?.length,
            inputFieldsCount: newInputFields?.length,
            revisionsCount: newRevisions?.length,
          });

          setTemplateLoadError(null);
          setTemplateData(newTemplateData);
          setTemplateBody(newTemplateBody);
          setInputFields(newInputFields);
          setDefinitionRevisions(newRevisions);
          setIsLoading(false);
          return data;
        }
        console.warn('[TemplateBuilder] Query returned no usable template definition');
        setTemplateLoadError(
          data == null
            ? 'No data returned from the server.'
            : 'Template definition was not found.'
        );
        setSupportValidationErrors([]);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('[TemplateBuilder] Query error:', {
          error,
          message: error?.message,
          graphQLErrors: error?.graphQLErrors,
          networkError: error?.networkError,
          extraInfo: error?.extraInfo,
          stack: error?.stack,
        });
        console.error('[TemplateBuilder] Full error object:', JSON.stringify(error, null, 2));
        setTemplateLoadError(graphqlErrorMessage(error));
        setSupportValidationErrors([]);
        setIsLoading(false);
      });
  };
  const user = useUser();

  const missingTemplatePaths = useMemo(() => {
    if (
      templateLoadError ||
      isLoading ||
      templateBody == null ||
      templateData == null
    ) {
      return [];
    }
    return validateTemplateBindings(templateBody, templateData).missingPaths;
  }, [templateBody, templateData, templateLoadError, isLoading]);

  const missingPathsKey = missingTemplatePaths.join(',');

  useEffect(() => {
    setLoadErrorDismissed(false);
  }, [templateLoadError]);

  useEffect(() => {
    setCompileErrorBannerDismissed(false);
  }, [bodyError]);

  useEffect(() => {
    setMissingFieldsBannerDismissed(false);
  }, [missingPathsKey]);

  const supportValidationErrorsKey = useMemo(
    () =>
      supportValidationErrors
        .map(e => `${e?.code ?? ''}:${e?.message ?? ''}`)
        .join('|'),
    [supportValidationErrors]
  );

  const hasSupportValidationErrors = supportValidationErrors.length > 0;

  const initializationTimeoutRef = useRef(null);
  const isUserTypingRef = useRef(false);
  const editorInitializedRef = useRef(false);
  const savedEditorStateRef = useRef(null);
  const lastUserEditValueRef = useRef(null);
  const editorUpdateTimeoutRef = useRef(null);
  const initializationAttemptedRef = useRef(false);

  // Save editor state (scroll position, cursor, focus)
  const saveEditorState = () => {
    if (monacoEditor.current) {
      const scrollTop = monacoEditor.current.getScrollTop();
      const scrollLeft = monacoEditor.current.getScrollLeft();
      const position = monacoEditor.current.getPosition();
      const hasTextFocus = document.activeElement === monacoEditor.current.getDomNode();
      
      savedEditorStateRef.current = {
        scrollTop,
        scrollLeft,
        position,
        hasTextFocus,
      };
    }
  };

  // Restore editor state (without aggressive scrolling to prevent shifts)
  const restoreEditorState = (aggressive = false, restoreCursor = false) => {
    if (monacoEditor.current && savedEditorStateRef.current) {
      const { scrollTop, scrollLeft, position, hasTextFocus } = savedEditorStateRef.current;
      
      // Always restore scroll position (this prevents jumps)
      if (scrollTop !== null && scrollTop !== undefined) {
        monacoEditor.current.setScrollTop(scrollTop);
      }
      if (scrollLeft !== null && scrollLeft !== undefined) {
        monacoEditor.current.setScrollLeft(scrollLeft);
      }
      
      // Only restore cursor position if explicitly requested (for external updates)
      // Don't restore cursor during normal typing - it causes shifts
      if (restoreCursor && position) {
        monacoEditor.current.setPosition(position);
        // Only reveal in center if aggressive mode (for external updates)
        // Otherwise just ensure it's visible without centering (prevents shifts)
        if (aggressive) {
          monacoEditor.current.revealPositionInCenter(position);
        } else {
          monacoEditor.current.revealPosition(position);
        }
      }
      
      // Restore focus if it was previously focused
      if (hasTextFocus) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          monacoEditor.current?.focus();
        });
      }
    }
  };

  useEffect(() => {
    console.log('[TemplateBuilder] useEffect triggered:', {
      hasTemplateData: !!templateData,
      hasTemplateBody: !!templateBody,
      definitionId,
      isLoading,
      editorInitialized: editorInitializedRef.current,
      editorExists: !!monacoEditor.current,
      initializationAttempted: initializationAttemptedRef.current,
    });

    // If editor already exists and is initialized, skip initialization
    // Content updates are handled in a separate effect
    if (monacoEditor.current && editorInitializedRef.current) {
      console.log('[TemplateBuilder] Editor already exists and initialized, skipping initialization');
      return;
    }

    if (!templateData) {
      console.log('[TemplateBuilder] No templateData, calling getTemplateData');
      getTemplateData(null, user);
      initializationAttemptedRef.current = false; // Reset if templateData is lost
      return;
    }

    if (supportValidationErrors.length > 0) {
      return;
    }

    // Only attempt initialization if we have both templateData and templateBody
    // and we haven't already attempted initialization for this definitionId
    // Use != null so an empty string body still initializes Monaco (TYFR vs CHECKIN templates).
    if (
      templateData != null &&
      templateBody != null &&
      !initializationAttemptedRef.current
    ) {
      console.log('[TemplateBuilder] Both templateData and templateBody exist, initializing Monaco editor');
      console.log('[TemplateBuilder] Monaco container ref:', {
        hasRef: !!monacoContainer?.current,
        containerHeight: monacoContainer?.current?.clientHeight,
        containerWidth: monacoContainer?.current?.clientWidth,
      });
      
      // Mark that we've attempted initialization
      initializationAttemptedRef.current = true;
      
      // Wait for the container to be available in the DOM
      let retryCount = 0;
      const maxRetries = 50; // 5 seconds max (50 * 100ms)
      
      const initializeWhenReady = () => {
        // Check if editor was created while waiting
        if (monacoEditor.current && editorInitializedRef.current) {
          console.log('[TemplateBuilder] Editor was created while waiting, stopping initialization');
          if (initializationTimeoutRef.current) {
            clearTimeout(initializationTimeoutRef.current);
            initializationTimeoutRef.current = null;
          }
          return;
        }

        if (monacoContainer?.current && monacoContainer.current.clientHeight > 0 && monacoContainer.current.clientWidth > 0) {
          console.log('[TemplateBuilder] Container is ready, initializing Monaco editor');
          initializationTimeoutRef.current = null;
          initializeMonacoEditor(templateBody);
        } else if (retryCount < maxRetries) {
          retryCount++;
          console.log('[TemplateBuilder] Container not ready yet, retrying in 100ms', {
            hasRef: !!monacoContainer?.current,
            height: monacoContainer?.current?.clientHeight,
            width: monacoContainer?.current?.clientWidth,
            retryCount,
            maxRetries,
          });
          initializationTimeoutRef.current = setTimeout(initializeWhenReady, 100);
        } else {
          console.error('[TemplateBuilder] Failed to initialize Monaco editor: container not available after max retries');
          initializationTimeoutRef.current = null;
          initializationAttemptedRef.current = false; // Reset so we can try again
        }
      };
      
      // Start the initialization check
      initializeWhenReady();
    } else if (templateBody == null) {
      console.log('[TemplateBuilder] Waiting for templateBody to become available');
    }

    return () => {
      console.log('[TemplateBuilder] useEffect cleanup', {
        editorExists: !!monacoEditor.current,
        editorInitialized: editorInitializedRef.current,
      });
      
      // Clear any pending initialization timeout
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
        initializationTimeoutRef.current = null;
      }
      
      // Only dispose editor if definitionId changed (new template) or component unmounting
      // Don't dispose on every re-render - that's what's causing the issue!
      // The editor should persist across re-renders
    };
  }, [
    templateData,
    templateBody,
    definitionId,
    supportValidationErrorsKey,
  ]);

  useEffect(() => {
    if (supportValidationErrors.length === 0) {
      return;
    }
    if (initializationTimeoutRef.current) {
      clearTimeout(initializationTimeoutRef.current);
      initializationTimeoutRef.current = null;
    }
    if (monacoEditor.current) {
      monacoEditor.current.dispose();
      monacoEditor.current = null;
      editorInitializedRef.current = false;
    }
    disposables.current.forEach(disposable => {
      disposable.dispose();
    });
    disposables.current = [];
    initializationAttemptedRef.current = false;
  }, [supportValidationErrorsKey, supportValidationErrors.length]);

  // Separate effect for content updates - only when editor exists and templateBody changes externally
  useEffect(() => {
    // Only run if editor exists and is initialized
    if (!monacoEditor.current || !editorInitializedRef.current) {
      return;
    }

    const currentEditorValue = monacoEditor.current.getValue();
    const editorHasFocus = document.activeElement === monacoEditor.current.getDomNode();
    
    // Never update the editor model if:
    // 1. User is actively typing
    // 2. Editor has focus (user might be editing)
    // 3. The current editor value matches what the user last edited
    const shouldSkipUpdate = 
      isUserTypingRef.current || 
      editorHasFocus || 
      (lastUserEditValueRef.current && currentEditorValue === lastUserEditValueRef.current);
    
    // Only update if content is different and we should not skip
    if (currentEditorValue !== templateBody && !shouldSkipUpdate) {
      console.log('[TemplateBuilder] Updating editor content from external source');
      
      // Save scroll position and cursor position BEFORE any update
      saveEditorState();
      
      // Update the model
      const model = monacoEditor.current.getModel();
      if (model) {
        model.setValue(templateBody);
        
        // Restore scroll and cursor position immediately after update (restore cursor for external updates)
        requestAnimationFrame(() => {
          restoreEditorState(false, true); // Restore cursor for external updates
        });
      }
    }
  }, [templateBody]); // Only depends on templateBody for external updates

  // Reset initialization flag when definitionId changes
  useEffect(() => {
    initializationAttemptedRef.current = false;
    console.log('[TemplateBuilder] definitionId changed, resetting initialization flag');
  }, [definitionId]);

  // Separate cleanup effect that only runs on unmount or definitionId change
  useEffect(() => {
    return () => {
      console.log('[TemplateBuilder] Component unmounting or definitionId changed, disposing editor');
      
      // Clear any pending editor update timeout
      if (editorUpdateTimeoutRef.current) {
        clearTimeout(editorUpdateTimeoutRef.current);
        editorUpdateTimeoutRef.current = null;
      }
      
      // Only dispose when actually unmounting or definitionId changes
      if (monacoEditor.current) {
        monacoEditor.current.dispose();
        monacoEditor.current = null;
        editorInitializedRef.current = false;
      }
      disposables.current.forEach(disposable => {
        disposable.dispose();
      });
    };
  }, [definitionId]); // Only run cleanup when definitionId changes or component unmounts

  // Only restore editor state when needed (not on every render)
  // This prevents visual shifts during typing
  useLayoutEffect(() => {
    // Only restore if:
    // 1. Editor exists and is initialized
    // 2. We have saved state
    // 3. User is NOT actively typing (to prevent shifts during typing)
    // 4. Editor doesn't have focus (user isn't editing)
    if (monacoEditor.current && editorInitializedRef.current && savedEditorStateRef.current) {
      const editorHasFocus = document.activeElement === monacoEditor.current.getDomNode();
      
      // Don't restore during active typing or when editor has focus - it causes visual shifts
      if (!isUserTypingRef.current && !editorHasFocus) {
        // Use requestAnimationFrame to ensure DOM is stable and prevent visual shifts
        requestAnimationFrame(() => {
          if (monacoEditor.current && savedEditorStateRef.current && !isUserTypingRef.current) {
            const stillHasFocus = document.activeElement === monacoEditor.current.getDomNode();
            if (!stillHasFocus) {
              restoreEditorState(false, false); // Only restore scroll, not cursor to prevent visual shifts
            }
          }
        });
      }
    }
  }, [templateBody, templateData]); // Only run when these change

  const editorOptions = {
    mode: { name: 'handlebars', base: 'text/html' },
    minimap: {
      enabled: false,
    },
    wordWrap: 'on',
    lineNumbers: true,
  };

  const computeRecipientEmailForPreview = () => {
    const fromProp =
      guestPrimaryEmail != null && String(guestPrimaryEmail).trim() !== ''
        ? String(guestPrimaryEmail).trim()
        : '';
    return (
      fromProp || getGuestPrimaryEmailFromTemplateData(templateData) || ''
    );
  };

  const openPreviewSend = () => {
    const { subject, bodyHtml } = parseSubjectAndBodyFromEmailHtml(emailHtml);
    setPreviewBodyHtml(bodyHtml);
    setPreviewSubject(subject);
    setPreviewTo(computeRecipientEmailForPreview());
    setPreviewCc(
      formatGuestCcDefault(
        guestEmailAddresses,
        computeRecipientEmailForPreview()
      )
    );
    setPreviewSendOpen(true);
  };

  // Refill To when guestPrimaryEmail / templateContext arrives after first paint (Apollo timing).
  useEffect(() => {
    if (!showPreviewSendButton || !previewSendOpen) return;
    const fromProp =
      guestPrimaryEmail != null && String(guestPrimaryEmail).trim() !== ''
        ? String(guestPrimaryEmail).trim()
        : '';
    const next =
      fromProp || getGuestPrimaryEmailFromTemplateData(templateData) || '';
    if (!next) return;
    setPreviewTo(prev => (prev && prev.trim() !== '' ? prev : next));
  }, [showPreviewSendButton, previewSendOpen, guestPrimaryEmail, templateData]);

  useEffect(() => {
    if (!showPreviewSendButton || !previewSendOpen) return;
    const next = formatGuestCcDefault(
      guestEmailAddresses,
      computeRecipientEmailForPreview()
    );
    if (!next) return;
    setPreviewCc(prev => (prev && prev.trim() !== '' ? prev : next));
  }, [
    showPreviewSendButton,
    previewSendOpen,
    guestEmailAddresses,
    guestPrimaryEmail,
    templateData,
  ]);

  const handleSendEmail = async () => {
    if (!client) {
      alert('Unable to send email: Apollo client is not available.');
      return;
    }
    const to = (previewTo || '').trim();
    if (!to) {
      alert('Please enter a recipient email address.');
      return;
    }
    const { subject: derivedFromBody, bodyHtml: messageHtml } =
      parseSubjectAndBodyFromEmailHtml(previewBodyHtml);
    const subject =
      (previewSubject || '').trim() ||
      derivedFromBody ||
      '(no subject)';
    const confirmed = window.confirm(
      'This will send an email now, and once sent there is no going back. Do you want to continue?'
    );
    if (!confirmed) return;
    setSendEmailLoading(true);
    try {
      await client.mutate({
        mutation: SEND_EMAIL,
        variables: {
          to,
          subject,
          message: messageHtml,
          cc: parseCcStringForMutation(
            previewCc,
            computeRecipientEmailForPreview()
          ),
        },
      });
      alert('Email sent.');
      setPreviewSendOpen(false);
    } catch (err) {
      alert(graphqlErrorMessage(err));
    } finally {
      setSendEmailLoading(false);
    }
  };

  return (
    <Mutation
      mutation={UPDATE_TEMPLATE_BUILDER_DEFINITION}
      variables={{ id: Number(definitionId), body: templateBody }}
    >
      {(updateDefinition, { data, loading: mutationLoading }) => {
        const tabsData = [
          {
            name: 'Docs',
            content: <HandlebarsExampleTabs />,
          },
          {
            name: 'Data',
            content: inputFields && (
              <div>
                {inputFields.map(field => {
                  const fieldName = field?.name;
                  const defaultValue = field?.value;
                  const fieldType = field?.type;
                  const options = field?.options;

                  const updateTemplate = e => {
                    const keyValuePair = {};
                    keyValuePair[fieldName] = e?.target?.value;
                    setTemplateData({ ...templateData, ...keyValuePair });
                  };

                  const templateValue = templateData && templateData[fieldName];

                  if (fieldType === 'INPUT') {
                    return (
                      <div key={fieldName} className="mb-2">
                        <label className="mb-1">{fieldName}</label>
                        <input
                          className="form-control"
                          defaultValue={templateValue || defaultValue}
                          onChange={e => {
                            e.persist();
                            const debounced = debounce(function() {
                              updateTemplate(e);
                            }, 500);
                            debounced();
                          }}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={fieldName} className="mb-2">
                      <label className="mb-1">{fieldName}</label>
                      <select
                        className="form-control"
                        name="platformKey"
                        defaultValue={defaultValue}
                        onChange={e => {
                          updateTemplate(e);
                        }}
                      >
                        <option hidden>Choose One</option>
                        {options?.map(option => {
                          return (
                            <option
                              // disabled={!platformIsSupported}
                              key={option?.value}
                              value={option?.value}
                            >
                              {option?.label}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })}
              </div>
            ),
          },
          {
            name: 'Revisions',
            content: (
              <div>
                <ul className="list-unstyled">
                  <ThemedButton
                    onSuccess={mutationFeedback?.success}
                    onClick={async () => {
                      const response = confirm(
                        `Are you sure? This will permanently save your template updates to the database. Note that you can modify the template specific to your needs and copy the output without updating the template in the database.`
                      );

                      if (response) {
                        if (mutationFeedback?.success)
                          setMutationFeedback({ success: false });

                        setUpdateTemplateLoading(true);
                        try {
                          const { data } = await updateDefinition({
                            id: Number(definitionId),
                            body: templateBody,
                            variables: {
                              TemplateBuilderDefinitionUpdateInput: {
                                body: templateBody,
                              },
                              TemplateBuilderDefinitionWhereUniqueInput: {
                                id: Number(definitionId),
                              },
                            },
                          });
                          if (data) {
                            setMutationFeedback({
                              success: true,
                              failure: false,
                            });
                          }
                        } catch (err) {
                          setMutationFeedback({ failure: true, success: false });
                          alert(err);
                        } finally {
                          setUpdateTemplateLoading(false);
                        }
                      }
                    }}
                    type="button"
                    loading={updateTemplateLoading}
                    className="btn btn-outline-primary"
                  >
                    Update Template
                  </ThemedButton>
                  {definitionRevisions?.map((definition, index) => {
                    return (
                      <li key={definition?.createdAt || index}>
                        <button
                          className="btn btn-link mb-0"
                          onClick={() => {
                            disposables.current.forEach(disposable => {
                              disposable.dispose();
                            });
                            setTemplateBody(definition?.body);
                            getTemplateData(definition?.body, user);
                          }}
                        >
                          {moment(definition?.createdAt).format('LLLL')}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ),
          },
        ];

        const compiledData = getCompiledHandlebarsTemplate(
          templateBody || '',
          templateData || {}
        );

        console.log('[TemplateBuilder] Render state:', {
          definitionId,
          isLoading,
          hasTemplateBody: !!templateBody,
          hasTemplateData: !!templateData,
          hasCompiledData: !!compiledData,
          hasMonacoContainer: !!monacoContainer?.current,
          hasMonacoEditor: !!monacoEditor?.current,
          bodyError,
        });

        return (
          // <HandlebarsProvider>
          <div id="template-builder" className="d-flex h-100 flex-column">
            {!isLoading && hasSupportValidationErrors && (
              <div className="flex-shrink-0 px-3 pt-3 pb-0 w-100">
                <div className="alert alert-warning mb-0" role="alert">
                  <div className="font-weight-bold mb-2">
                    This template cannot be used until the following is
                    resolved:
                  </div>
                  <ul className="mb-0 pl-3">
                    {supportValidationErrors.map((err, i) => (
                      <li key={err?.code || i}>
                        {err?.message ||
                          err?.code ||
                          'Validation error'}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <div
              className="d-flex flex-grow-1 position-relative"
              style={{ overflow: 'hidden', height: '100%' }}
            >
              {definitionId && <TabbedDrawer tabs={tabsData} />}
              {isLoading ? (
                <div
                  className="d-flex justify-content-center align-items-center w-100"
                  style={{ height: '70vh' }}
                >
                  <LoadingSpinner />
                </div>
              ) : hasSupportValidationErrors ? (
                <div className="flex-grow-1" aria-hidden="true" />
              ) : (
                <>
                  {' '}
                  <div id="monaco-template-wrapper" className="w-50 h-100 pb-3" style={{ overflow: 'hidden' }}>
                    <div
                      key="monaco-editor-container"
                      className="w-100 h-100"
                      ref={monacoContainer}
                      id="root"
                      style={{ height: '100%', overflow: 'hidden' }}
                    ></div>
                  </div>
                  <div className="w-50 h-100 position-relative d-flex flex-column">
                    {templateLoadError && !loadErrorDismissed && (
                      <div
                        className="alert alert-danger alert-dismissible mb-2"
                        role="alert"
                        style={{ flexShrink: 0 }}
                      >
                        <button
                          type="button"
                          className="close"
                          aria-label="Close"
                          onClick={() => setLoadErrorDismissed(true)}
                        >
                          <span aria-hidden="true">&times;</span>
                        </button>
                        {templateLoadError}
                      </div>
                    )}
                    {!templateLoadError &&
                      missingTemplatePaths.length > 0 &&
                      !missingFieldsBannerDismissed && (
                        <div
                          className="alert alert-warning alert-dismissible mb-2"
                          role="alert"
                          style={{ flexShrink: 0 }}
                        >
                          <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() =>
                              setMissingFieldsBannerDismissed(true)
                            }
                          >
                            <span aria-hidden="true">&times;</span>
                          </button>
                          Property name is missing from reservation data (
                          <code>appPropertyName</code>). Other fields may be absent by
                          design. Try refreshing the page, or verify the reservation in
                          the system.
                        </div>
                      )}
                    {!compiledData &&
                      bodyError &&
                      !compileErrorBannerDismissed && (
                        <div
                          className="alert alert-danger alert-dismissible mb-2"
                          role="alert"
                          style={{ flexShrink: 0 }}
                        >
                          <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() =>
                              setCompileErrorBannerDismissed(true)
                            }
                          >
                            <span aria-hidden="true">&times;</span>
                          </button>
                          Template compile error: {bodyError}
                        </div>
                      )}
                    {bodyError && (
                      <div
                        className="position-absolute"
                        style={{ right: '20px', top: '10px', zIndex: 10 }}
                      >
                        <Tooltip
                          label={bodyError}
                          placement="left"
                          id={`calendar-tooltip-template`}
                          style={{ maxWidth: '500px' }}
                        >
                          <AlertTriangle
                            width={30}
                            height={30}
                            className="alert-triangle-200"
                          />
                        </Tooltip>
                      </div>
                    )}
                    {/* Render compiled Handlebars output as HTML for rich copying (e.g. into Gmail) */}
                    
{/* 
                    <button
                    onClick={async () => {
                      if (welcomeSentLabel === 'Mark as Unsent') {
                        try {
                          const { data, loading } = await client.mutate({
                            mutation: MARK_WELCOME_AS_UNSENT,
                            variables: {
                              reference:
                                reference || urlParams.get('reference'),
                            },
                          });

                          setWelcomeSentMutationLoading(true);

                          if (data) {
                            setWelcomeSentLabel('Mark Sent');
                            setWelcomeSentMutationLoading(false);
                            alert('Your welcome message was marked as unsent!');
                            onSuccess();
                          }
                        } catch (err) {
                          console.log('err', err);
                          setWelcomeSentMutationLoading(false);
                          alert(
                            'There was a problem with updating the guest reservation. Please try again'
                          );
                        }
                        return;
                      }
                      setMarkAsSentModalIsOpen(true);
                      // try {
                      //   const { data, loading } = await client.mutate({
                      //     mutation: MARK_WELCOME_AS_SENT,
                      //     variables: {
                      //       reference: reference || urlParams.get('reference'),
                      //     },
                      //   });

                      //   setWelcomeSentMutationLoading(true);

                      //   if (data) {
                      //     setWelcomeSentLabel('Mark as Unsent');
                      //     setWelcomeSentMutationLoading(false);
                      //     // alert('Your welcome message was successfully sent!');
                      //     onSuccess();
                      //   }
                      // } catch (err) {
                      //   console.log('err', err);
                      //   setWelcomeSentMutationLoading(false);
                      //   alert(
                      //     'There was a problem with updating the guest reservation. Please try again'
                      //   );
                      // }
                    }}
                    className="btn btn-outline-primary ml-2"
                  >
                    {welcomeSentMutationLoading ? (
                      <div style={{ height: '20px' }}>
                        <LoadingSpinner
                          customDimensions={{ height: '20px', width: '20px' }}
                        />
                      </div>
                    ) : (
                      <> {welcomeSentLabel}</>
                    )}
                  </button>                     */}
                    {compiledData !== false && (
                      <div className="flex-grow-1 d-flex pb-1" style={{ minHeight: 0 }}>                        
                        <RichTextEditor
                          value={emailHtml}
                          onChange={html => setEmailHtml(html)}
                          placeholder=""
                          autoHeight={false}
                          showCopyButton={true}
                          copyButtonText="Copy Email HTML"
                          toolbarExtra={
                            showPreviewSendButton ? (
                              <ThemedButton
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={openPreviewSend}
                              >
                                Preview and Send
                              </ThemedButton>
                            ) : null
                          }
                          containerStyle={{  }}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              <div
                className={`alert alert-warning alert-dismissible position-absolute ${
                  alertShowTimeout ? 'fade-out' : 'd-none'
                }`}
                role="alert"
                style={{
                  right: '30px',
                  bottom: '0px',
                  minWidth: '200px',
                }}
              >
                <button
                  type="button"
                  className="close"
                  data-dismiss="alert"
                  aria-label="Close"
                  // onClick={() => {
                  //   setAlertShowTimeout(true);
                  //   setTimeout(() => {
                  //     setAlertShowTimeout(false);
                  //   }, 5000);
                  // }}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
                Copied to Clipboard!
              </div>
            </div>
           
            
            <Modal
              // large={true}
              isOpen={markAsSentModalIsOpen}
              title="Welcome Message Send Date"
              styleType="medium"
              disableForceFocus={true}
              closeOnOverlayClick={false}
              closeOnEsc={false}
              onClose={() => {
                setMarkAsSentModalIsOpen(false);
              }}
            >
              <WelcomeMessageSendForm
                reference={reference || urlParams.get('reference')}
                onSuccess={() => {
                  setWelcomeSentLabel('Mark as Unsent');
                  onSuccess();
                }}
              />
            </Modal>
            {showPreviewSendButton && (
              <Modal
                isOpen={previewSendOpen}
                title="Preview and Send"
                styleType="full"
                scrollable
                disableForceFocus={true}
                closeOnOverlayClick={false}
                closeOnEsc={false}
                customBodyStyles="p-0 d-flex flex-column flex-grow-1"
                onClose={() => {
                  setPreviewSendOpen(false);
                }}
              >
                <div
                  className="d-flex flex-column flex-grow-1 px-3 pb-3"
                  style={{ minHeight: 0, maxHeight: 'calc(100vh - 56px)' }}
                >
                  <div className="form-group mb-2 mt-2">
                    <label className="mb-1 font-weight-bold" htmlFor="preview-send-to">
                      To
                    </label>
                    <input
                      id="preview-send-to"
                      type="email"
                      className="form-control"
                      placeholder="recipient@example.com"
                      value={previewTo}
                      onChange={e => setPreviewTo(e.target.value)}
                      autoComplete="email"
                    />
                    {/* <small className="text-muted">
                    Prefilled from the reservation guest&apos;s{' '}
                    <code>primaryEmailAddress</code> when loaded on this page, or
                    from template context data when not.
                  </small> */}
                    <div className="text-muted small mt-1">
                      bcc: db@bhipmanagement.com, stacy@bhipmanagement.com, beachhousesinparadise@gmail.com
                    </div>
                  </div>
                  <div className="form-group mb-2">
                    <label
                      className="mb-1 font-weight-bold"
                      htmlFor="preview-send-cc"
                    >
                      Cc
                    </label>
                    <input
                      id="preview-send-cc"
                      type="text"
                      className="form-control"
                      placeholder="cc1@example.com, cc2@example.com"
                      value={previewCc}
                      onChange={e => setPreviewCc(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group mb-2">
                    <label
                      className="mb-1 font-weight-bold"
                      htmlFor="preview-send-subject"
                    >
                      Subject
                    </label>
                    <input
                      id="preview-send-subject"
                      type="text"
                      className="form-control"
                      placeholder="Derived from first &quot;Subject: …&quot; line in the message if present"
                      value={previewSubject}
                      onChange={e => setPreviewSubject(e.target.value)}
                    />
                    <small className="text-muted">
                      Defaults from the first line matching{' '}
                      <code>Subject: …</code> in the HTML (plain text). Edit as needed.
                    </small>
                  </div>
                  <div
                    className="d-flex flex-column flex-grow-1"
                    style={{ minHeight: 0 }}
                  >
                    <label className="font-weight-bold mb-1">Message (HTML)</label>
                    <div className="flex-grow-1 d-flex" style={{ minHeight: '50vh' }}>
                      <RichTextEditor
                        value={previewBodyHtml}
                        onChange={setPreviewBodyHtml}
                        placeholder=""
                        autoHeight={false}
                        showCopyButton={false}
                        height="100%"
                        containerStyle={{
                          width: '100%',
                          minHeight: '100%',
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-3 d-flex justify-content-end">
                    <ThemedButton
                      type="button"
                      className="btn btn-outline-secondary mr-2"
                      onClick={() => setPreviewSendOpen(false)}
                    >
                      Cancel
                    </ThemedButton>
                    <ThemedButton
                      type="button"
                      className="btn btn-primary"
                      loading={sendEmailLoading}
                      onClick={handleSendEmail}
                    >
                      Send email
                    </ThemedButton>
                  </div>
                </div>
              </Modal>
            )}
            <Modal
              isOpen={clipboardModalIsOpen}
              title="Copied to Clipboard!"
              // styleType="full"
              disableForceFocus={true}
              closeOnOverlayClick={false}
              closeOnEsc={false}
              onClose={() => {
                setClipboardModalIsOpen(false);
              }}
            >
              <div>
                <p>
                  Send the message to the guest and then click the button below.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    try {
                      const { data, loading } = await client.mutate({
                        mutation: MARK_WELCOME_AS_SENT,
                        variables: {
                          reference,
                        },
                      });

                      setWelcomeSentMutationLoading(true);
                      if (data) {
                        setWelcomeSentMutationLoading(false);
                        alert('Your welcome message was successfully sent!');
                        onSuccess();
                      }
                    } catch (err) {
                      console.log('err', err);
                      setWelcomeSentMutationLoading(true);
                      alert(
                        'There was a problem with updating the guest reservation. Please try again'
                      );
                    }
                  }}
                >
                  {welcomeSentMutationLoading ? (
                    <LoadingSpinner />
                  ) : (
                    'I sent the message to the guest!'
                  )}
                </button>
              </div>
            </Modal>
          </div>
          // </HandlebarsProvider>
        );
      }}
    </Mutation>
  );
};

TemplateBuilder.defaultProps = {
  onSuccess: () => {},
  guestPrimaryEmail: undefined,
  guestEmailAddresses: undefined,
  showPreviewSendButton: false,
};

export default withApollo(TemplateBuilder);
